import { useEffect, useRef, useState } from "react";
import { getSquarePayments, isSquareSandbox } from "@/lib/squarePayments";

/**
 * SquareWalletButtons — renders Apple Pay + Google Pay buttons that share
 * Square's Web Payments SDK. Each button tokenizes the wallet's card and
 * emits the source_id via `onTokenized(source_id, walletName)`. The parent
 * then POSTs to /orders/pay with that source_id (same code path as card).
 *
 * Both wallets gracefully hide themselves when unsupported (e.g. Google Pay
 * won't load on iOS Safari; Apple Pay won't load on Chrome/Android without
 * a valid Apple Pay session). No JS errors either way.
 */
export default function SquareWalletButtons({
  total, // dollars — must be a positive number > 0
  onTokenized,
  onError,
  disabled = false,
}) {
  const applePayBtnRef = useRef(null);
  const googlePayBtnRef = useRef(null);
  const paymentRequestRef = useRef(null);
  const applePayRef = useRef(null);
  const googlePayRef = useRef(null);
  const [applePayReady, setApplePayReady] = useState(false);
  const [googlePayReady, setGooglePayReady] = useState(false);
  const [walletBusy, setWalletBusy] = useState(false);
  const [diag, setDiag] = useState([]);
  const initializedRef = useRef(false);
  const showDiag = typeof window !== "undefined" && /[?&]walletdebug=1/.test(window.location.search);
  const note = (msg) => {
    // eslint-disable-next-line no-console
    console.warn("[wallets]", msg);
    setDiag((d) => [...d, msg]);
  };

  // Initialize SDK + wallet instances once the total is known.
  const hasTotal = Number(total) > 0;
  useEffect(() => {
    if (!hasTotal || initializedRef.current) return;
    initializedRef.current = true;
    let mounted = true;
    const appId = process.env.REACT_APP_SQUARE_APPLICATION_ID;
    const locationId = process.env.REACT_APP_SQUARE_LOCATION_ID;
    const isSandbox = isSquareSandbox();
    if (!appId || !locationId) { note("Square app/location id missing in frontend env"); return; }

    (async () => {
      try {
        const payments = await getSquarePayments();
        if (!mounted) return;
        note(`SDK loaded (${isSandbox ? "sandbox" : "production"}) · host ${window.location.host}`);

        const paymentRequest = payments.paymentRequest({
          countryCode: "US",
          currencyCode: "USD",
          total: {
            amount: total.toFixed(2),
            label: "Blossom's Kitchen",
          },
        });
        paymentRequestRef.current = paymentRequest;

        // Apple Pay: only initializes in Safari/WebKit with Wallet set up AND a Square-verified domain.
        try {
          if (!window.ApplePaySession) {
            note("Apple Pay: this browser has no ApplePaySession (needs Safari / iOS WebKit)");
          } else if (!window.ApplePaySession.canMakePayments()) {
            note("Apple Pay: device can't make payments (no card in Wallet?)");
          } else {
            const applePay = await payments.applePay(paymentRequest);
            if (mounted) {
              applePayRef.current = applePay;
              setApplePayReady(true);
              note("Apple Pay: ready");
            }
          }
        } catch (e) {
          note(`Apple Pay init failed: ${e?.message || e}`);
        }

        // Google Pay: attaches its own branded button into the always-rendered container.
        try {
          const googlePay = await payments.googlePay(paymentRequest);
          if (mounted && googlePayBtnRef.current) {
            await googlePay.attach(googlePayBtnRef.current, {
              buttonColor: "black",
              buttonType: "long",
              buttonSizeMode: "fill",
            });
            googlePayRef.current = googlePay;
            setGooglePayReady(true);
            note("Google Pay: ready");
          }
        } catch (e) {
          note(`Google Pay init failed: ${e?.message || e}`);
        }
      } catch (e) {
        note(`SDK failed: ${e?.message || e}`);
        onError && onError(e.message || "Wallet payments unavailable");
      }
    })();

    return () => {
      mounted = false;
      initializedRef.current = false;
      if (googlePayRef.current && googlePayRef.current.destroy) {
        googlePayRef.current.destroy().catch(() => {});
      }
      if (applePayRef.current && applePayRef.current.destroy) {
        applePayRef.current.destroy().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTotal]);

  // Keep the wallet's paymentRequest in sync with the current total (tip / reward changes).
  useEffect(() => {
    const pr = paymentRequestRef.current;
    if (!pr || !total || total <= 0) return;
    try {
      pr.update({
        total: { amount: total.toFixed(2), label: "Blossom's Kitchen" },
      });
    } catch {
      /* older SDK versions may not expose update() — safe to ignore */
    }
  }, [total]);

  const tokenizeWith = async (instance, walletName) => {
    if (!instance || walletBusy || disabled) return;
    setWalletBusy(true);
    try {
      const result = await instance.tokenize();
      if (result.status === "OK") {
        onTokenized(result.token, walletName);
      } else if (result.status !== "Cancel") {
        const msg =
          (result.errors || []).map((e) => e.message).join(", ") ||
          `${walletName} payment failed`;
        onError && onError(msg);
      }
      // status === "Cancel" → user backed out; silent no-op
    } catch (e) {
      onError && onError(e.message || `${walletName} payment failed`);
    } finally {
      setWalletBusy(false);
    }
  };

  const anyReady = applePayReady || googlePayReady;

  return (
    <div className={anyReady ? "space-y-2" : ""} data-testid="square-wallet-buttons">
      {anyReady && (
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black">
          Express checkout
        </div>
      )}

      {applePayReady && (
        <button
          ref={applePayBtnRef}
          type="button"
          onClick={() => tokenizeWith(applePayRef.current, "Apple Pay")}
          disabled={disabled || walletBusy}
          data-testid="apple-pay-btn"
          aria-label="Pay with Apple Pay"
          className="w-full h-11 rounded-lg bg-black text-white flex items-center justify-center font-medium disabled:opacity-40 hover:bg-stone-800 active:scale-[0.98] transition-all"
          style={{ fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          <span className="text-sm">Pay with </span>
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="w-5 h-5 ml-1 fill-white"
          >
            <path d="M17.05 11.97c-.06-3.05 2.49-4.51 2.6-4.58-1.42-2.07-3.63-2.36-4.41-2.39-1.88-.19-3.66 1.1-4.61 1.1-.96 0-2.42-1.08-3.98-1.05-2.05.03-3.94 1.19-4.99 3.02-2.13 3.69-.55 9.15 1.53 12.15 1.02 1.47 2.24 3.12 3.83 3.06 1.54-.06 2.12-.99 3.99-.99 1.85 0 2.4.99 4.03.95 1.66-.03 2.72-1.5 3.74-2.98 1.18-1.7 1.66-3.34 1.68-3.42-.04-.02-3.22-1.23-3.26-4.86zm-3.11-8.94c.84-1.03 1.42-2.44 1.26-3.86-1.22.05-2.71.83-3.59 1.85-.78.9-1.48 2.36-1.3 3.75 1.37.11 2.78-.7 3.63-1.74z" />
          </svg>
        </button>
      )}

      <div
        ref={googlePayBtnRef}
        data-testid="google-pay-btn"
        className={googlePayReady ? "w-full min-h-[44px]" : "hidden"}
        onClickCapture={(e) => {
          e.stopPropagation();
          tokenizeWith(googlePayRef.current, "Google Pay");
        }}
      />

      {anyReady && (
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-stone-400 font-black pt-1">
          <div className="flex-1 h-px bg-stone-200" />
          or use a card
          <div className="flex-1 h-px bg-stone-200" />
        </div>
      )}

      {showDiag && (
        <pre className="text-[10px] leading-snug whitespace-pre-wrap bg-stone-900 text-green-300 rounded p-2 mt-2" data-testid="wallet-diag">
          {diag.length ? diag.join("\n") : "wallets: initializing…"}
        </pre>
      )}
    </div>
  );
}
